import { BrandedFooter } from '@components/branding';
import {
    ChecklistGroup,
    ChecklistProvider,
    ChecklistTarget
} from '@components/checklist';
import {
    ChecklistContext,
    IChecklistContext
} from '@components/checklist/checklist-context';
import { ConditionalError } from '@components/conditional-error';
import { FieldCheckBox } from '@components/field-builder/field-checkbox';
import { RequiredWrapper } from '@components/required';
import Certification from '@containers/form-i9/certification';
import FormI9DocumentDetailList from '@containers/form-i9/document-details-list';
import EmployeeInfoVerification from '@containers/form-i9/employee-info-verification';
import {
    FormSectionDivider,
    SectionBox,
    SectionHeading,
    SectionSubtitle
} from '@containers/form-i9/styles';
import USCISFormI9Section2Link from '@containers/links/uscis-formI9-section2-link';
import USCISWebsiteLink from '@containers/links/uscis-website-link';
import { FieldType } from '@models/field-builder/field-type';
import { ISection1CandidateDetail } from '@models/onboarding-review/section1-candidate-detail';
import { Section2ValidationErrors } from '@models/onboarding-review/section2-validation-errors';
import { CompleteI9PageTranslation } from '@screens/review-section2/complete-i9/complete-i9-page-translation';
import { AdditionalInformation } from '@screens/review-section2/complete-i9/styles';
import { FormI9Container } from '@screens/review-section2/employee-information/styles';
import {
    Buttons,
    DashedDivider,
    FocusedError
} from '@screens/review-section2/styles';
import {
    AgreementTitle,
    InstructionLine,
    RoundedContainer,
    Title
} from '@screens/review-section3/review/styles';
import {
    CenterContainer,
    Content,
    Header
} from '@screens/review-section3/styles';
import { IConfigurationValuesService } from '@services/ConfigurationValuesService';
import { IErrorReportingService } from '@services/error-reporting-service';
import {
    createConfigurationValuesService,
    createErrorReportingService,
    createTranslationService
} from '@services/factories';
import { ESignResult } from '@services/onboarding-review/e-sign-request-service';
import { ISection2CertificationStore } from '@stores/onboarding-review/section2-certification-store';
import { ISection2CompleteStore } from '@stores/onboarding-review/section2-complete-store';
import { ISection2DocumentsStore } from '@stores/onboarding-review/section2-documents-store';
import { ScrollContainer } from '@styles/container-styles';
import { ConfirmRowButton, RejectRowButton } from '@styles/shared-styles';
import { VerticalLayout } from '@styles/spacing-styles';
import { cacheTranslations } from '@utils/cache-translations';
import { StringFormatService } from '@services/StringFormatService';
import TrackingService, { ITrackingService } from '@services/TrackingService';
import { ClientTrackingCategory } from '@models/client-tracking-category';
import { ClientTrackingName } from '@models/client-tracking-name';
import { reaction } from 'mobx';
import { observer, Observer } from 'mobx-react';
import * as React from 'react';
import * as Scroll from 'react-scroll';
import { isNil } from 'lodash';

interface IProps {
    candidateDetails: ISection1CandidateDetail;
    store: ISection2CompleteStore;
    certificationStore: ISection2CertificationStore;
    documentsStore: ISection2DocumentsStore;
    eSignResult: ESignResult;
    validationErrors: Record<Section2ValidationErrors, boolean>;
    validateForm: () => boolean;
    onESign: () => void;
    onDecline: () => void;
    onBack: () => void;
}

enum GlobalTranslation {
    todaysDateError = 'todaysDateError',
    eSignValidationErrorMessage = 'eSignValidationErrorMessage',
    requestFailure = 'requestFailure'
}

class CompleteI9Page extends React.Component<IProps> {
    readonly translations = cacheTranslations(
        createTranslationService(),
        CompleteI9PageTranslation,
        'completeI9'
    );

    readonly validationErrorTranslations = cacheTranslations(
        createTranslationService(),
        Section2ValidationErrors,
        'completeI9ValidationErrors'
    );

    readonly globalTranslations = cacheTranslations(
        createTranslationService(),
        GlobalTranslation
    );

    readonly scrollContainerId = 'complete-i9-page';
    readonly checklistGroups = [
        'document-details',
        'additional-info',
        'certification'
    ];
    readonly checklistContext: IChecklistContext = new ChecklistContext(
        this.checklistGroups
    );
    readonly configurationValuesService: IConfigurationValuesService =
        createConfigurationValuesService();
    readonly errorReportingService: IErrorReportingService =
        createErrorReportingService();
    private readonly trackingService: ITrackingService = new TrackingService();

    componentDidMount() {
        reaction(
            () =>
                this.props.validationErrors.documentNumberDoesNotMatchSection1,
            (value) => {
                if (value) {
                    const ssnDocumentNumberField =
                        this.checklistContext.getTargetByCriteria({
                            fieldType: 'document-number',
                            isSSN: true
                        });

                    if (ssnDocumentNumberField) {
                        this.smoothScrollToField(ssnDocumentNumberField);
                    }
                }
            }
        );
    }

    scrollToFirstInvalidField = () => {
        const firstInvalidField = this.checklistContext.getFirstInvalidTarget();
        if (!firstInvalidField) {
            return false;
        }

        this.smoothScrollToField(firstInvalidField);
        return true;
    };

    private smoothScrollToField(field: string) {
        Scroll.scroller.scrollTo(field, {
            smooth: true,
            containerId: this.scrollContainerId
        });
    }

    handleESignClicked = () => {
        const { certificationStore, candidateDetails } = this.props;

        if (!this.props.validateForm()) {
            this.trackingService.trackEvent(
                ClientTrackingCategory.DocumentValidation,
                ClientTrackingName.Error
            );
            if (!this.scrollToFirstInvalidField()) {
                this.errorReportingService.reportError(
                    new Error('Could not find invalid field'),
                    {
                        tags: {
                            flow: 'section2',
                            category: 'validation'
                        }
                    }
                );
            }

            return;
        }

        if (
            certificationStore.firstName.trim() ===
                candidateDetails.firstName.trim() &&
            certificationStore.lastName.trim() ===
                candidateDetails.lastName.trim()
        ) {
            alert(
                'Authorized representative name cannot be same as candidates name'
            );
            return;
        }

        this.props.onESign();
    };

    handleDeclineClicked = () => {
        // todo: fill in implementation
        this.props.onDecline();
    };

    handleBackLinkClicked = () => this.props.onBack();

    renderAdditionalInformation = (listForm: string) => {
        const { store, documentsStore, validationErrors } = this.props;
        const hideDocumentsPhysicallyExamined =
            this.configurationValuesService.hideDocumentsPhysicallyExamined;

        if (hideDocumentsPhysicallyExamined) {
            return (
                <AdditionalInformation>
                    {this.translations.additionalInformation}
                </AdditionalInformation>
            );
        }

        return (
            <React.Fragment>
                <RequiredWrapper>
                    <AdditionalInformation>
                        {this.translations.additionalInformation}
                    </AdditionalInformation>
                </RequiredWrapper>
                {/* {renderDocumentDetails()} */}
                <ChecklistTarget
                    name={`documents-physically-examined-${listForm}`}
                    group={this.checklistGroups[1]}
                    valid={store.documentsPhysicallyExamined}
                >
                    <Observer>
                        {() => (
                            <FieldCheckBox
                                type={FieldType.CHECKBOX}
                                id={'documents-physically-examined-check'}
                                value={store.documentsPhysicallyExamined}
                                label={
                                    <RequiredWrapper>
                                        {
                                            this.translations
                                                .documentsPhysicallyExaminedLabel
                                        }
                                    </RequiredWrapper>
                                }
                                helperText={
                                    <ConditionalError
                                        condition={
                                            !store.documentsPhysicallyExamined &&
                                            validationErrors.confirmDocumentsPhysicallyExamined
                                        }
                                    >
                                        {
                                            this.validationErrorTranslations
                                                .confirmDocumentsPhysicallyExamined
                                        }
                                    </ConditionalError>
                                }
                                setValueMethod={
                                    store.setDocumentsPhysicallyExamined
                                }
                            />
                        )}
                    </Observer>
                </ChecklistTarget>
            </React.Fragment>
        );
    };

    renderConditionallyAdditionalInformation = (listForm: string) => {
        const { store, documentsStore, validationErrors } = this.props;

        return (
            <ChecklistTarget
                name={`documents-physically-examined-${listForm}`}
                group={this.checklistGroups[1]}
                valid={store.documentsPhysicallyExamined}
            >
                <Observer>
                    {() => (
                        <FieldCheckBox
                            type={FieldType.CHECKBOX}
                            id={'documents-physically-examined-check'}
                            value={store.documentsPhysicallyExamined}
                            label={
                                <RequiredWrapper>
                                    {
                                        this.translations
                                            .documentsPhysicallyExaminedLabel
                                    }
                                </RequiredWrapper>
                            }
                            helperText={
                                <ConditionalError
                                    condition={
                                        !store.documentsPhysicallyExamined &&
                                        validationErrors.confirmDocumentsPhysicallyExamined
                                    }
                                >
                                    {
                                        this.validationErrorTranslations
                                            .confirmDocumentsPhysicallyExamined
                                    }
                                </ConditionalError>
                            }
                            setValueMethod={
                                store.setDocumentsPhysicallyExamined
                            }
                        />
                    )}
                </Observer>
            </ChecklistTarget>
        );
    };

    render() {
        const {
            candidateDetails,
            store,
            documentsStore,
            certificationStore,
            eSignResult,
            validationErrors
        } = this.props;

        const showSection1 = this.configurationValuesService.showSection1;
        const workerStatus = documentsStore.applicantStatus;

        return (
            <ScrollContainer id={this.scrollContainerId}>
                <ChecklistProvider value={this.checklistContext}>
                    <CenterContainer
                        fillAvailable={true}
                        constrainToParentWidth={true}
                    >
                        <FormI9Container>
                            {workerStatus !== 'RECEIPT_REPLACEMENT' && (
                                <Header>
                                    {/* Instructions Interpolation */}
                                    {/* Step Label */}
                                    <Title
                                        topSpacing={true}
                                        bottomSpacing={true}
                                    >
                                        {this.translations.stepInstruction}
                                    </Title>
                                    {/* USCIS Website */}
                                    <InstructionLine>
                                        {this.translations.uscisDescription}{' '}
                                        <USCISWebsiteLink />
                                    </InstructionLine>
                                    {/* USCIS Section 2 */}
                                    <InstructionLine>
                                        {this.translations.section2Description}{' '}
                                        <USCISFormI9Section2Link />
                                    </InstructionLine>
                                </Header>
                            )}
                            <Content>
                                <FormSectionDivider />
                                <VerticalLayout
                                    innerSpacing={'large'}
                                    bottomSpacing={'large'}
                                >
                                    <div>
                                        <SectionBox>
                                            <SectionHeading>
                                                {
                                                    this.translations
                                                        .i9Section2Heading
                                                }
                                            </SectionHeading>
                                            <br />
                                            <SectionSubtitle>
                                                {
                                                    this.translations
                                                        .i9Section2Subtitle
                                                }
                                            </SectionSubtitle>
                                        </SectionBox>
                                        <EmployeeInfoVerification
                                            store={candidateDetails}
                                        />
                                    </div>
                                    <div>
                                        {workerStatus ===
                                            'RECEIPT_REPLACEMENT' &&
                                            StringFormatService.format(
                                                this.translations
                                                    .receiptReplacementTitle,
                                                [candidateDetails.firstName]
                                            )}
                                    </div>
                                    {/* List A */}
                                    {/* List B */}
                                    {/* List C */}
                                    {/* Additional Information */}
                                    <ChecklistGroup
                                        name={this.checklistGroups[0]}
                                    >
                                        <FormI9DocumentDetailList
                                            store={documentsStore}
                                            validationErrors={validationErrors}
                                            renderAdditionalInformation={
                                                this.renderAdditionalInformation
                                            }
                                            renderConditionallyAdditionalInformation={
                                                this
                                                    .renderConditionallyAdditionalInformation
                                            }
                                            qrCodeSection={
                                                <React.Fragment>
                                                    <div>
                                                        {
                                                            this.translations
                                                                .qrCodeSection2And3Line1
                                                        }
                                                    </div>
                                                    <div>
                                                        {
                                                            this.translations
                                                                .qrCodeSection2And3Line2
                                                        }
                                                    </div>
                                                </React.Fragment>
                                            }
                                        />
                                    </ChecklistGroup>
                                    {/* Certification */}
                                    <ChecklistGroup
                                        name={this.checklistGroups[2]}
                                    >
                                        <Title>
                                            {this.translations.certification}
                                        </Title>
                                        <div>
                                            <Certification
                                                store={certificationStore}
                                                validationErrors={
                                                    validationErrors
                                                }
                                                workerStatus={workerStatus}
                                            />
                                        </div>
                                        {/* ESignature Checkbox and Agreement */}
                                        <RoundedContainer>
                                            <AgreementTitle>
                                                <RequiredWrapper>
                                                    {
                                                        this.translations
                                                            .eSignatureByProvidingSignature
                                                    }
                                                </RequiredWrapper>
                                            </AgreementTitle>
                                            <ChecklistTarget
                                                name={'esignature-agreement'}
                                                valid={
                                                    store.acceptedElectronicSignature
                                                }
                                            >
                                                <FieldCheckBox
                                                    type={FieldType.CHECKBOX}
                                                    id={'e-signature-agreement'}
                                                    label={
                                                        <React.Fragment>
                                                            <p>
                                                                <RequiredWrapper>
                                                                    {
                                                                        this
                                                                            .translations
                                                                            .eSignatureLine1
                                                                    }
                                                                </RequiredWrapper>
                                                            </p>
                                                            <p>
                                                                <RequiredWrapper>
                                                                    {
                                                                        this
                                                                            .translations
                                                                            .eSignatureLine2
                                                                    }
                                                                </RequiredWrapper>
                                                            </p>
                                                            <p>
                                                                <RequiredWrapper>
                                                                    {
                                                                        this
                                                                            .translations
                                                                            .eSignatureLine3
                                                                    }
                                                                </RequiredWrapper>
                                                            </p>
                                                        </React.Fragment>
                                                    }
                                                    helperText={
                                                        <ConditionalError
                                                            condition={
                                                                !store.acceptedElectronicSignature &&
                                                                validationErrors.confirmESignAgreement
                                                            }
                                                        >
                                                            {
                                                                this
                                                                    .validationErrorTranslations
                                                                    .confirmESignAgreement
                                                            }
                                                        </ConditionalError>
                                                    }
                                                    setValueMethod={
                                                        store.setAcceptedElectronicSignature
                                                    }
                                                    value={
                                                        store.acceptedElectronicSignature
                                                    }
                                                />
                                            </ChecklistTarget>
                                        </RoundedContainer>
                                    </ChecklistGroup>
                                    {eSignResult === ESignResult.Failed && (
                                        <FocusedError>
                                            {
                                                this.globalTranslations
                                                    .requestFailure
                                            }
                                        </FocusedError>
                                    )}
                                    <div>
                                        <DashedDivider />
                                        <Buttons innerSpacing={'small'}>
                                            <RejectRowButton
                                                onClick={
                                                    this.handleDeclineClicked
                                                }
                                            >
                                                {
                                                    this.translations
                                                        .declineButton
                                                }
                                            </RejectRowButton>
                                            <ConfirmRowButton
                                                data-testid='confirmEsignButton'
                                                onClick={
                                                    this.handleESignClicked
                                                }
                                            >
                                                {this.translations.esignButton}
                                            </ConfirmRowButton>
                                        </Buttons>
                                        {/* Back link */}
                                        {showSection1 &&
                                             (!isNil(workerStatus) &&
                                             workerStatus !== '') && (
                                                <span
                                                    onClick={
                                                        this
                                                            .handleBackLinkClicked
                                                    }
                                                >
                                                    {
                                                        this.translations
                                                            .backButton
                                                    }
                                                </span>
                                            )}
                                    </div>
                                </VerticalLayout>
                            </Content>
                        </FormI9Container>
                    </CenterContainer>
                    <BrandedFooter />
                </ChecklistProvider>
            </ScrollContainer>
        );
    }
}

export default observer(CompleteI9Page);

// adding test comment trigger build to resolve build issue of I9ARP-26



// WEBPACK FOOTER //
// ./src/screens/review-section2/complete-i9/complete-i9-page.tsx